import * as THREE from 'three';
import Stats from 'stats.js';
import { World } from './game/World';
import { Terrain } from './game/Terrain';
import { CameraRig } from './game/CameraRig';
import { Grass } from './render/Grass';
import { Rocks } from './render/Rocks';
import { RocksGLB } from './render/RocksGLB';
import { SkirtHills } from './render/SkirtHills';
import { Post } from './render/Post';
import { AntWorld } from './sim/AntWorld';
import { AntColony } from './render/AntColony';
import { Colony } from './sim/Colony';
import { BroodRender } from './render/BroodRender';
import { SpiderSim } from './sim/Spider';
import { SpiderMesh } from './render/SpiderMesh';
import { GrasshopperSim } from './sim/Grasshopper';
import { ObstacleGrid } from './sim/ObstacleGrid';
import type { Obstacle } from './sim/Collision';
import { GrasshopperMesh } from './render/GrasshopperMesh';
import { RolyPolySim } from './sim/RolyPoly';
import { RolyPolyMesh } from './render/RolyPolyMesh';
import { LizardSim } from './sim/Lizard';
import { LizardMesh } from './render/LizardMesh';
import { FoodField } from './sim/FoodField';
import { PheromoneGrid } from './sim/PheromoneGrid';
import { FoodMarkers } from './render/FoodMarkers';
import { NestMarker } from './render/NestMarker';
import { NestInterior } from './render/NestInterior';
import { NestMode } from './game/NestMode';
import { Input } from './game/Input';
import { Minimap } from './ui/Minimap';
import { PopulationHUD } from './ui/PopulationHUD';
import { ColonyHUD } from './ui/ColonyHUD';
import { PlayerHUD } from './ui/PlayerHUD';
import { WeatherHUD } from './ui/WeatherHUD';
import { KeyHelp } from './ui/KeyHelp';
import { buildTouchControls } from './ui/TouchControls';
import { buildFullscreenButton } from './ui/FullscreenButton';
import { buildSoundControls } from './ui/SoundControls';
import { GameAudio } from './ui/Audio';
import { loadTuneStore, applyTuneOverrides } from './ui/TuneStore';
import { loadWorld, saveWorld, defaultWorldConfig, type FoodPilePlacement } from './ui/WorldStore';
import { loadCatalog } from './ui/CatalogStore';
import { setAssetOverrides } from './ui/AssetMap';
import { CustomCreatureSim } from './sim/CustomCreature';
import { CustomCreatureMesh } from './render/CustomCreatureMesh';
import { CustomPlantMesh } from './render/CustomPlantMesh';
import { Pebbles } from './sim/Pebbles';
import { PebbleMesh } from './render/PebbleMesh';
import { loadShadowConfig } from './ui/ShadowStore';
import { loadTerrainConfig } from './ui/TerrainStore';
import { loadSoundConfig } from './ui/SoundStore';
import { loadFoodConfig } from './ui/FoodStore';
import { Weather } from './sim/Weather';
import { Rain } from './render/Rain';
import { MagnifyingGlass } from './render/MagnifyingGlass';
import { BugSpray } from './render/BugSpray';
import { LawnMower } from './render/LawnMower';
import { FootStomp } from './render/FootStomp';
import type { NestConfig } from './sim/contracts';

const IS_MOBILE = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const ANTS_PER_COLONY = 60;
// Mobile Safari chokes on too many skinned-mesh clones; cap lower there.
const MAX_PER_COLONY = IS_MOBILE ? 200 : 600;
// Renderer needs enough skinned-mesh clones to cover the worst case + queens.
const ANT_COUNT = MAX_PER_COLONY * 2 + 4;
const MAX_FOOD_PILES = 32;

/** Map a terrain-preset id to a set of fbm knobs. `custom` (and anything
 *  unrecognised) passes the saved values through untouched. */
function presetTerrainKnobs(t?: { preset?: string; amplitude?: number; frequency?: number; octaves?: number; persistence?: number }) {
  const custom = {
    amplitude: t?.amplitude ?? 0.8,
    frequency: t?.frequency ?? 0.08,
    octaves: t?.octaves ?? 4,
    persistence: t?.persistence ?? 0.5,
  };
  switch (t?.preset) {
    case 'flat':        return { amplitude: 0.05, frequency: 0.03, octaves: 2, persistence: 0.4 };
    case 'rolling':     return { amplitude: 0.5,  frequency: 0.06, octaves: 4, persistence: 0.5 };
    case 'hilly':       return { amplitude: 1.2,  frequency: 0.05, octaves: 5, persistence: 0.55 };
    case 'mountainous': return { amplitude: 2.4,  frequency: 0.04, octaves: 6, persistence: 0.6 };
    case 'cracked-desert': return { amplitude: 0.3, frequency: 0.18, octaves: 4, persistence: 0.7 };
    case 'custom':
    default:            return custom;
  }
}

async function boot() {
  const mount = document.getElementById('app')!;
  const loading = document.getElementById('loading')!;
  const loadingFill = document.getElementById('loading-fill');
  const loadingStep = document.getElementById('loading-step');
  const loadingLabel = document.getElementById('loading-label');

  // Named boot stages — total step count is the source of truth for the bar.
  const stages = [
    'Fetching server config',
    'Starting renderer',
    'Loading sky',
    'Carving terrain',
    'Growing grass',
    'Placing rocks',
    'Opening nests',
    'Scattering food',
    'Hatching ants',
    'Waking spiders + grasshoppers',
    'Final setup',
  ];
  let stageIdx = 0;
  const advance = (label?: string) => {
    stageIdx++;
    const pct = Math.round((stageIdx / stages.length) * 100);
    if (loadingFill) loadingFill.style.width = `${pct}%`;
    if (loadingStep) loadingStep.textContent = `${pct}%`;
    if (loadingLabel && label) loadingLabel.textContent = label;
  };
  const setStage = (label: string) => {
    if (loadingLabel) loadingLabel.textContent = label;
  };

  const audio = new GameAudio();

  setStage(stages[0]);
  await loadTuneStore();
  const serverWorld = await loadWorld();
  const catalog = await loadCatalog();
  // Fresh install safety net: if the server has no catalog entries yet (before
  // the editor has seeded one), fall back to the built-in roster so the world
  // still boots with spiders / grasshoppers / roly-polies / lizard / mower /
  // grass / rocks present.
  if (catalog.creatures.length === 0 && catalog.plants.length === 0) {
    const { seededCatalog } = await import('./ui/CatalogStore');
    Object.assign(catalog, seededCatalog());
  }
  // Per-world transform overrides. For any catalog entry with a `tuneKey`
  // (system critters — spider, grasshopper, grass, etc.), if the world
  // sets any of scale/rot/offY we merge those on top of the global tune
  // cache. The renderers' readTune(tuneKey, ...) calls pick up the
  // world-specific values without ever writing to /api/tune. Tiny spiders
  // in one world, huge ones in another — no cross-world leak.
  {
    const overrides: Record<string, { scale?: number; rotX?: number; rotY?: number; rotZ?: number; offY?: number }> = {};
    for (const entry of [...catalog.creatures, ...catalog.plants]) {
      if (!entry.tuneKey) continue;
      const ref = serverWorld?.worldEntries?.[entry.id];
      if (!ref) continue;
      const o: Record<string, number> = {};
      if (ref.scale !== undefined) o.scale = ref.scale;
      if (ref.rotX  !== undefined) o.rotX  = ref.rotX;
      if (ref.rotY  !== undefined) o.rotY  = ref.rotY;
      if (ref.rotZ  !== undefined) o.rotZ  = ref.rotZ;
      if (ref.offY  !== undefined) o.offY  = ref.offY;
      if (Object.keys(o).length > 0) overrides[entry.tuneKey] = o;
    }
    applyTuneOverrides(overrides);
  }
  // Resolve per-world entry state for a catalog id. If the world has no entry
  // for this id yet (new catalog addition), treat it as enabled with the
  // catalog's default count.
  const resolveRef = (id: string, fallbackCount: number) => {
    const ref = serverWorld?.worldEntries?.[id];
    if (!ref) return { enabled: true, count: fallbackCount };
    return { enabled: ref.enabled, count: ref.count };
  };
  // Redirect per-slot asset URLs to any uploads the current world specifies.
  // MUST happen before any `new *Mesh(...)` constructor that calls
  // GLTFLoader — otherwise the defaults have already been fetched.
  setAssetOverrides(serverWorld?.assetOverrides);
  const shadowCfg = await loadShadowConfig();
  const terrainCfg = await loadTerrainConfig();
  const soundCfg = await loadSoundConfig();
  audio.setMix(soundCfg);
  // Per-world sound overrides — clip URLs from the sound library that
  // replace the default procedural one-shots / built-in MP3 loops.
  audio.setSoundOverrides(serverWorld?.soundOverrides ?? {});
  const foodCfg = await loadFoodConfig();
  advance(stages[1]);

  const world = new World(mount);
  await world.loadEnvironment();
  advance(stages[2]);
  world.applyShadowConfig(shadowCfg);
  audio.attachUnlock(world.renderer.domElement);

  // Two nests — positions come from the world config. Defaults mirror the
  // old hard-coded layout so un-configured worlds still work.
  const redCfg = serverWorld?.colonies?.[0];
  const blackCfg = serverWorld?.colonies?.[1];
  const redNest:   NestConfig = {
    x: redCfg?.nestX ?? -22, z: redCfg?.nestZ ?? 0, radius: redCfg?.nestRadius ?? 0.7,
  };
  const blackNest: NestConfig = {
    x: blackCfg?.nestX ?? 22, z: blackCfg?.nestZ ?? 0, radius: blackCfg?.nestRadius ?? 0.7,
  };
  const craters = [
    { x: redNest.x,   z: redNest.z,   radius: 1.2, depth: 0.9 },
    { x: blackNest.x, z: blackNest.z, radius: 1.2, depth: 0.9 },
  ];

  setStage(stages[3]);
  const terrainBlock = serverWorld?.terrain;
  // Translate preset → (amplitude, frequency, octaves, persistence) so the
  // user can pick "Hilly" and get something sensible without touching the
  // raw knobs. Custom preset lets the saved values pass through.
  const procKnobs = presetTerrainKnobs(terrainBlock);
  const terrain = new Terrain({
    size: (terrainBlock?.worldRadius ?? 40) * 2,
    segments: 256,
    seed: serverWorld?.terrainSeed ?? terrainBlock?.seed ?? 1337,
    craters,
    amplitude: procKnobs.amplitude,
    frequency: procKnobs.frequency,
    octaves: procKnobs.octaves,
    persistence: procKnobs.persistence,
    type: terrainCfg.type,
    heightmapUrl: terrainBlock?.heightmapUrl,
    heightmapScale: terrainBlock?.heightmapScale ?? 2,
    textureUrl: terrainBlock?.textureUrl,
    textureTileRepeat: terrainBlock?.textureTileRepeat ?? 1,
  });
  await terrain.load();
  world.scene.add(terrain.mesh);
  if (serverWorld?.terrain?.enabled === false) terrain.mesh.visible = false;
  // Distant rolling-hills skirt — pure decoration that fades into the
  // sky via fog, smoothing the visual transition past the playable
  // square. Seeded from the terrain seed so each world has consistent
  // distant geometry across reloads.
  const skirt = new SkirtHills({
    worldRadius: terrainBlock?.worldRadius ?? 40,
    seed: serverWorld?.terrainSeed ?? terrainBlock?.seed ?? 1337,
  });
  world.scene.add(skirt.mesh);
  advance(stages[4]);

  const camera = new CameraRig(world.renderer.domElement, terrain);
  world.onResize(camera.resize);

  // Apply starting camera view from the world config. Skip if a zeroed
  // placeholder sneaks in (default config uses a non-zero view anyway).
  {
    const cb = serverWorld?.camera;
    if (cb) {
      camera.camera.position.set(cb.posX, cb.posY, cb.posZ);
      // Snap the orbit target's Y up to actual ground level. World
      // configs typically save targetY=0 (sea level), but worlds with
      // a tall heightmap (no-food's heightmapScale ~5) put the ground
      // well above 0. Orbiting around a target buried under terrain
      // makes the camera dip below the surface from certain angles —
      // the back-face-culled terrain reads as transparent and the user
      // sees "under the terrain". Lifting the target to ground level
      // avoids that.
      const groundY = terrain.getHeight(cb.targetX, cb.targetZ);
      camera.controls.target.set(cb.targetX, Math.max(cb.targetY, groundY), cb.targetZ);
      if (cb.fovDeg && cb.fovDeg > 10 && cb.fovDeg < 120) {
        camera.camera.fov = cb.fovDeg;
        camera.camera.updateProjectionMatrix();
      }
      camera.controls.update();
    }
  }

  const grassEntry = catalog.plants.find((p) => p.systemPlant === 'grass');
  const grassRef = grassEntry
    ? resolveRef(grassEntry.id, grassEntry.defaultCount)
    : { enabled: true, count: 4000 };
  const grass = new Grass(terrain, {
    count: grassRef.enabled ? grassRef.count : 0,
    maxDistance: 22,
    placements: serverWorld?.grass,
  });
  await grass.load();
  world.scene.add(grass.mesh);
  if (!grassRef.enabled) grass.mesh.visible = false;
  advance(stages[5]);

  const rocksEntry = catalog.plants.find((p) => p.systemPlant === 'rocks');
  const rocksRef = rocksEntry
    ? resolveRef(rocksEntry.id, rocksEntry.defaultCount)
    : { enabled: true, count: 120 };
  const rocks = new RocksGLB(terrain, {
    count: rocksRef.enabled ? rocksRef.count : 0,
    seed: 42,
    placements: serverWorld?.rocks,
  });
  await rocks.load();
  world.scene.add(rocks.group);
  if (!rocksRef.enabled) rocks.group.visible = false;
  void Rocks;
  advance(stages[6]);

  const redNestMarker = new NestMarker(redNest, terrain);
  const blackNestMarker = new NestMarker(blackNest, terrain);
  world.scene.add(redNestMarker.group);
  world.scene.add(blackNestMarker.group);

  const redInterior = new NestInterior(redNest, 0);
  const blackInterior = new NestInterior(blackNest, 1);
  // Keep the interior groups OFF the scene while we're on the surface. They
  // get attached/detached in NestMode.descend/ascend, so the surface render
  // path doesn't even see them.

  const food = new FoodField();
  // If the server has a saved food layout use it verbatim; otherwise generate
  // one, save it, and all later clients share the same piles.
  const initialFoodPiles: FoodPilePlacement[] = serverWorld?.foodPiles ?? [];
  if (initialFoodPiles.length === 0) {
    // First boot — generate a ring of food piles around each nest using
    // the per-world food block. Sugar/leaf colours still come from the
    // global FoodStore (foodCfg below).
    const wf = serverWorld?.food ?? { pilesPerColony: 6, amountMin: 30, amountMax: 100 };
    const pilesPerColony = wf.pilesPerColony;
    const range = Math.max(0, wf.amountMax - wf.amountMin);
    for (const n of [redNest, blackNest]) {
      for (let i = 0; i < pilesPerColony; i++) {
        const a = (i / pilesPerColony) * Math.PI * 2 + Math.random() * 0.4;
        const r = 4 + Math.random() * 8;
        const px = n.x + Math.cos(a) * r;
        const pz = n.z + Math.sin(a) * r;
        const sugar = i % 2 === 0;
        initialFoodPiles.push({
          x: px, z: pz,
          amount: wf.amountMin + Math.floor(Math.random() * (range + 1)),
          value: sugar ? 5 : 3,
          kind: 'pile',
        });
      }
    }
  }
  for (const fp of initialFoodPiles) {
    food.spawn(fp.x, fp.z, fp.amount, fp.value, fp.kind);
  }
  advance(stages[7]);
  const foodMarkers = new FoodMarkers(food, terrain, MAX_FOOD_PILES, {
    sugarColor: foodCfg.sugarColor,
    leafColor: foodCfg.leafColor,
  });
  foodMarkers.syncInstances();
  world.scene.add(foodMarkers.group);

  const pheromones = [new PheromoneGrid(), new PheromoneGrid()];

  const antWorld = new AntWorld(terrain, [redNest, blackNest], food, pheromones, {
    // Square half-extent the ants are clamped to. Match the world's own
    // half-extent so the entire playable square is reachable — corners
    // included.
    wanderRadius: serverWorld?.terrain?.worldRadius ?? 40,
    maxSpeed: serverWorld?.sim?.antSpeed,
    onEvent: (e) => {
      if (e === 'bite') audio.playBite();
      else if (e === 'death') audio.playDeath();
      else if (e === 'pickup') audio.playPickup();
    },
    casteHp: serverWorld?.casteHp,
  });
  antWorld.setObstacles(rocks.obstacles);
  antWorld.setFreezeDeath(serverWorld?.climate?.freezeDeath ?? true);

  // Grass tufts as soft circular obstacles — tight per-ant checks would
  // obliterate perf if done against all 4 k tufts, so wrap them in a spatial
  // grid and hand each sim the `.near()` accessor.
  const grassObstacles: Obstacle[] = grass.generatedPlacements.map((p) => ({
    x: p.x, z: p.z, radius: 0.08 * p.scale,
  }));
  const grassGrid = new ObstacleGrid(grassObstacles, 1.0);
  const nearbyGrass = (x: number, z: number) => grassGrid.near(x, z);
  // Pebbles are built further down (they need the world radius + color from
  // the world config), but ants need them as obstacles from the first tick.
  // Late-bind via a ref so the callback can reference the Pebbles instance
  // once it exists.
  const pebblesRef: { inst: import('./sim/Pebbles').Pebbles | null } = { inst: null };
  const nearbyGrassAndPebbles = (x: number, z: number) => {
    const a = nearbyGrass(x, z);
    const b = pebblesRef.inst ? pebblesRef.inst.near(x, z) : [];
    if (b.length === 0) return a;
    const merged: Array<{ x: number; z: number; radius: number }> = [];
    for (let i = 0; i < a.length; i++) merged.push(a[i]);
    for (let i = 0; i < b.length; i++) merged.push(b[i]);
    return merged;
  };
  antWorld.setNearbyObstacles(nearbyGrassAndPebbles);

  const redStart = redCfg?.enabled === false ? 0
    : Math.max(1, redCfg?.startingPopulation ?? ANTS_PER_COLONY);
  const blackStart = blackCfg?.enabled === false ? 0
    : (blackCfg?.startingPopulation ?? ANTS_PER_COLONY);
  for (let i = 0; i < redStart; i++) {
    if (i === 0) {
      // First spawn becomes the player. Place well outside the nest
      // descend-trigger radius (0.4 m) so we don't auto-dive on boot —
      // especially on iPhone where touch input can't move the ant away
      // from the hole before the first frame runs.
      antWorld.spawn(redNest.x + 1.2, redNest.z, 0);
      continue;
    }
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * 0.5;
    antWorld.spawn(redNest.x + Math.cos(a) * r, redNest.z + Math.sin(a) * r, 0);
  }
  for (let i = 0; i < blackStart; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.random() * 0.5;
    antWorld.spawn(blackNest.x + Math.cos(a) * r, blackNest.z + Math.sin(a) * r, 1);
  }

  setStage(stages[8]);
  const colony = new AntColony(antWorld, terrain, ANT_COUNT);
  await colony.load();
  world.scene.add(colony.group);
  advance(stages[9]);

  // Per-team colony trackers (food stockpile, queen id, brood lifecycle).
  const maxPop = serverWorld?.sim?.maxAntsPerColony ?? MAX_PER_COLONY;
  const redColony = new Colony(0, redNest, { maxPopulation: maxPop });
  const blackColony = new Colony(1, blackNest, { maxPopulation: maxPop });
  // Spawn a queen at each nest and register her id with the colony.
  const redQueenId = antWorld.spawn(redNest.x, redNest.z, 0, 'queen');
  redColony.setQueen(redQueenId);
  const blackQueenId = antWorld.spawn(blackNest.x, blackNest.z, 1, 'queen');
  blackColony.setQueen(blackQueenId);
  // Seed each colony with some initial food so they don't starve at boot.
  redColony.addFood(redCfg?.startingStockpile ?? 40);
  blackColony.addFood(blackCfg?.startingStockpile ?? 40);
  // Ants dropping food at the nest feed their colony's stockpile.
  antWorld.onFoodDelivered = (team, amount) => {
    (team === 0 ? redColony : blackColony).addFood(amount);
  };
  // Hungry ants eat from the colony stockpile when they enter the nest.
  antWorld.onAntEat = (team, cost) => {
    const c = team === 0 ? redColony : blackColony;
    if (c.stockpile < cost) return false;
    c.stockpile -= cost;
    return true;
  };

  const spiderEntry = catalog.creatures.find((c) => c.systemSim === 'spider');
  const spiderRef = spiderEntry
    ? resolveRef(spiderEntry.id, spiderEntry.defaultCount)
    : { enabled: false, count: 0 };
  const spiderCount = spiderRef.enabled ? spiderRef.count : 0;
  const spiderSim = new SpiderSim(terrain, antWorld, {
    count: spiderCount, patrolRadius: 30,
    obstacles: rocks.obstacles,
    nearbyObstacles: nearbyGrass,
    food,
  });
  const spiderMesh = new SpiderMesh(spiderSim, terrain);
  world.scene.add(spiderMesh.group);
  antWorld.setSpiderTargets(spiderSim.spiders);

  const grasshopperEntry = catalog.creatures.find((c) => c.systemSim === 'grasshopper');
  const grasshopperRef = grasshopperEntry
    ? resolveRef(grasshopperEntry.id, grasshopperEntry.defaultCount)
    : { enabled: false, count: 0 };
  const hopperCount = grasshopperRef.enabled ? grasshopperRef.count : 0;
  const grasshopperSim = new GrasshopperSim(terrain, antWorld, {
    count: hopperCount,
    patrolRadius: 30,
    spiders: spiderSim.spiders,
    food,
    obstacles: rocks.obstacles,
    nearbyObstacles: nearbyGrass,
  });
  const grasshopperMesh = new GrasshopperMesh(grasshopperSim, terrain);
  world.scene.add(grasshopperMesh.group);
  // Ants still get drawn to grasshoppers via the aggro hook, but Grasshopper.update
  // handles the actual damage + food drop now.
  antWorld.setGrasshopperTargets(grasshopperSim.hoppers, () => {});

  const rolyPolyEntry = catalog.creatures.find((c) => c.systemSim === 'rolyPoly');
  const rolyPolyRef = rolyPolyEntry
    ? resolveRef(rolyPolyEntry.id, rolyPolyEntry.defaultCount)
    : { enabled: false, count: 0 };
  const rolyCount = rolyPolyRef.enabled ? rolyPolyRef.count : 0;
  const rolyPolySim = new RolyPolySim(terrain, {
    count: rolyCount, patrolRadius: 30,
    obstacles: rocks.obstacles,
    nearbyObstacles: nearbyGrass,
  });
  const rolyPolyMesh = new RolyPolyMesh(rolyPolySim, terrain);
  world.scene.add(rolyPolyMesh.group);

  // User-authored creatures. Each catalog creature enabled in this world
  // gets its own sim + renderer; we feed them on the same update cadence
  // as the built-in fauna. Per-world count + enabled come from
  // serverWorld.worldEntries; everything else is sourced from the catalog.
  // Entries tagged with `systemSim` are handled by the specialised sims
  // above (spider / grasshopper / rolyPoly / lizard / mower) and skipped
  // here.
  const customSims: Array<{ sim: CustomCreatureSim; mesh: CustomCreatureMesh }> = [];
  const worldR = serverWorld?.terrain?.worldRadius ?? 40;
  const worldEntries = serverWorld?.worldEntries ?? {};
  for (const entry of catalog.creatures) {
    if (entry.systemSim) continue; // handled by specialised sim
    const ref = worldEntries[entry.id] ?? { enabled: false, count: entry.defaultCount };
    if (!ref.enabled) continue;
    if (!entry.glbUrl) continue;
    // Per-world overrides win over the catalog defaults — this is what
    // the /insects?world=<slug> tuner saves into worldEntries[id].
    const cfg = {
      id: entry.id,
      label: entry.label,
      glbUrl: entry.glbUrl,
      enabled: true,
      count: ref.count,
      scale: ref.scale ?? entry.scale,
      speed: entry.speed,
      behavior: entry.behavior,
      hp: entry.hp,
      bitePerSec: entry.bitePerSec,
      rotX: ref.rotX ?? entry.rotX,
      rotY: ref.rotY ?? entry.rotY,
      rotZ: ref.rotZ ?? entry.rotZ,
      offY: ref.offY ?? entry.offY,
    };
    const sim = new CustomCreatureSim(cfg, terrain, antWorld, worldR);
    const mesh = new CustomCreatureMesh(sim, terrain);
    world.scene.add(mesh.group);
    customSims.push({ sim, mesh });
  }

  // Pebble scatter — small clickable debris. Player picks one up, drops
  // it elsewhere, nearby workers mimic in a frenzy. Color + count come
  // from the world config so each world looks different.
  const pebbleCfg = serverWorld?.pebbles ?? { enabled: true, count: 600 };
  const pebbles = new Pebbles(terrain, worldR, pebbleCfg.enabled ? pebbleCfg.count : 0);
  pebblesRef.inst = pebbles;
  const pebbleMesh = new PebbleMesh(pebbles, terrain);
  if (!pebbleCfg.enabled) pebbleMesh.group.visible = false;
  world.scene.add(pebbleMesh.group);
  antWorld.setPebbleHooks({
    pickup: (x, z, r, holder) => pebbles.pickupNearest(x, z, r, holder),
    drop: (idx, x, z) => pebbles.drop(idx, x, z),
    closest: (x, z) => pebbles.findClosestFree(x, z),
    posOf: (idx) => pebbles.items[idx] ? { x: pebbles.items[idx].x, z: pebbles.items[idx].z } : null,
  });

  // User-authored plants — static scatter. No sim, no per-frame tick.
  // `systemPlant` entries (grass / rocks) are handled by the specialised
  // renderers above and skipped here.
  const customPlants: CustomPlantMesh[] = [];
  for (const entry of catalog.plants) {
    if (entry.systemPlant) continue; // handled by specialised renderer
    const ref = worldEntries[entry.id] ?? { enabled: false, count: entry.defaultCount };
    if (!ref.enabled) continue;
    if (!entry.glbUrl) continue;
    // Per-world overrides apply here too. The /insects tune panel
    // exposes a single "Scale" slider — when set, it replaces both
    // scaleMin and scaleMax with that value (variance lost, but the
    // user explicitly picked one number). Rotation + Y offset
    // override the catalog values directly.
    const refScale = ref.scale;
    const cfg = {
      id: entry.id,
      label: entry.label,
      glbUrl: entry.glbUrl,
      enabled: true,
      count: ref.count,
      scaleMin: refScale ?? entry.scaleMin,
      scaleMax: refScale ?? entry.scaleMax,
      alignTerrain: entry.alignTerrain,
      isPack: entry.isPack,
      rotX: ref.rotX ?? entry.rotX,
      rotY: ref.rotY ?? entry.rotY,
      rotZ: ref.rotZ ?? entry.rotZ,
      offY: ref.offY ?? entry.offY,
    };
    const mesh = new CustomPlantMesh(cfg, terrain, worldR);
    world.scene.add(mesh.group);
    customPlants.push(mesh);
  }

  // Once every uploaded plant has loaded its GLB and scattered its
  // instances, fold their per-instance obstacles into the global rock
  // list so AntWorld's collision pass treats them as solids. Without
  // this, ants walk straight through the piaggio (and any other
  // user-uploaded prop). We do this in the background — boot continues
  // and the obstacles light up a few frames later.
  void Promise.all(customPlants.map((m) => m.ready)).then(() => {
    const merged: Array<{ x: number; z: number; radius: number }> = [];
    for (const o of rocks.obstacles) merged.push(o);
    for (const m of customPlants) for (const o of m.obstacles) merged.push(o);
    antWorld.setObstacles(merged);
  });

  // Periodic lizard visitor — shows up on day 2, every 2 days after. If he
  // wanders off the map he's gone for 3 days. Eats any ant or spider he
  // brushes past. Lizard count is always 1 — only the enabled flag is
  // catalog-driven.
  const lizardEntry = catalog.creatures.find((c) => c.systemSim === 'lizard');
  const lizardRef = lizardEntry
    ? resolveRef(lizardEntry.id, lizardEntry.defaultCount)
    : { enabled: false, count: 0 };
  const lizardEnabled = lizardRef.enabled;
  const lizardSim = new LizardSim(terrain, antWorld, spiderSim, {
    firstDay: 2,
    recurrenceDays: 2,
    banishmentDays: 3,
    worldRadius: 38,
  });
  const lizardMesh = new LizardMesh(lizardSim, terrain);
  world.scene.add(lizardMesh.group);

  // Brood is now rendered INSIDE each nest's queen chamber — the surface
  // never shows eggs/larvae. Keep the sim-side BroodRender for update plumbing
  // (its group stays hidden forever).
  const brood = new BroodRender([redColony, blackColony], [redNest, blackNest], terrain);
  brood.group.visible = false;
  advance(stages[10]);

  const magGlass = new MagnifyingGlass(terrain);
  magGlass.attach(world.renderer.domElement as HTMLCanvasElement, camera.camera);
  world.scene.add(magGlass.group);

  const bugSpray = new BugSpray(terrain);
  bugSpray.attach(world.renderer.domElement as HTMLCanvasElement, camera.camera);
  world.scene.add(bugSpray.group);

  const mowerEntry = catalog.creatures.find((c) => c.systemSim === 'mower');
  const mowerRef = mowerEntry
    ? resolveRef(mowerEntry.id, mowerEntry.defaultCount)
    : { enabled: false, count: 0 };
  const mowerEnabled = mowerRef.enabled;
  const mower = new LawnMower(terrain);
  world.scene.add(mower.group);

  const foot = new FootStomp(terrain, { intervalMin: 60, intervalMax: 180 });
  world.scene.add(foot.group);

  const input = new Input(
    world.renderer.domElement as HTMLCanvasElement,
    camera.camera,
    terrain.mesh,
    antWorld,
    {
      focusPlayer: () => {
        const p = antWorld.ants[antWorld.playerId];
        if (p) camera.flyTo(p.x, p.y, p.z, 1.0);
      },
      toggleFollowPlayer: () => {
        if (camera.isFollowing()) {
          camera.setFollow(null);
        } else {
          camera.setFollow(() => {
            const p = antWorld.ants[antWorld.playerId];
            return p ? { x: p.x, y: p.y, z: p.z } : null;
          });
        }
      },
      toggleAntCam: () => {
        if (camera.isAntCam()) {
          camera.setAntCam(null);
        } else {
          camera.setAntCam(() => {
            const p = antWorld.ants[antWorld.playerId];
            return p ? { x: p.x, y: p.y, z: p.z, heading: p.heading } : null;
          });
        }
      },
      goHome: () => {
        antWorld.setPlayerGoal(redNest.x, redNest.z);
      },
      toggleKeyHelp: () => {
        keyHelp.toggle();
      },
    },
  );
  input.addClickable(rocks.group);
  input.addClickable(grass.mesh);
  // Pebble click: if the click hit a pebble OR the player is carrying one,
  // consume the click; otherwise let the normal move-to flow run.
  input.setPebbleHandler(pebbleMesh.group, (x, z, hitPebble, _instanceId, isDouble) => {
    const p = antWorld.ants[antWorld.playerId];
    if (!p) return false;
    const carrying = p.heldPebble !== -1;
    // Double-click while carrying — walk to the spot and drop there.
    if (isDouble && carrying) {
      antWorld.setPlayerDropGoal(x, z);
      return true;
    }
    // Single click on a pebble while not carrying — resolve the actual
    // pebble from the world-space hit point. The raycaster's instanceId
    // is per-variant InstancedMesh and meaningless across the 5 buckets,
    // so we always go by closest-free at the hit, which IS the clicked
    // stone.
    if (hitPebble && !carrying) {
      const idx = pebbles.findClosestFree(x, z);
      if (idx === -1) return false;
      const pos = pebbles.items[idx];
      antWorld.setPlayerPickupGoal(pos.x, pos.z, idx);
      return true;
    }
    return false;
  });
  void input;

  const minimapSize = IS_MOBILE ? 140 : 280;
  const minimap = new Minimap(antWorld, food, antWorld, [redNest, blackNest], {
    size: minimapSize,
    worldExtent: serverWorld?.terrain?.worldRadius ?? 40,
    spiders: spiderSim,
    grasshoppers: grasshopperSim,
    rolyPolies: rolyPolySim,
    lizard: lizardSim,
    mower,
    undergroundSideView: () => {
      if (!nestMode.isUnderground) return null;
      const side = nestMode.active;
      if (!side) return null;
      const interior = side === 'red' ? redInterior : blackInterior;
      return interior.sideViewData();
    },
    onExitUnderground: () => nestMode.ascendFromInput(),
    nestPreview: {
      scene: world.scene,
      getFocus: () => {
        if (!nestMode.isUnderground) return null;
        const interior = nestMode.active === 'red' ? redInterior : blackInterior;
        const mid = interior.cameraView.target;
        return { x: mid.x, y: mid.y, z: mid.z };
      },
    },
    // Clicking the minimap always zooms the camera to that spot. If follow
    // mode was on, it's released first so the camera stays where you looked.
    onMapClick: (x, z) => {
      if (camera.isFollowing()) camera.setFollow(null);
      camera.flyTo(x, terrain.getHeight(x, z), z, 0.7);
    },
  });
  const popHud = new PopulationHUD(antWorld);
  const playerHud = new PlayerHUD(antWorld);
  // Single-player: only the RED colony is controllable; BLACK auto-pilots.
  // Later multiplayer can pass both colonies here.
  const colonyHud = new ColonyHUD([redColony], antWorld);

  // Weather — day/night cycle, wind gusts, and occasional rain that washes trails.
  // All timings pull from the world's climate block when a config is loaded.
  const climate = serverWorld?.climate;
  const DAY_LENGTH = climate?.dayLengthSec ?? 600;
  const weather = new Weather({
    dayLength: DAY_LENGTH,
    initialTimeOfDay: (climate?.startTimeOfDay ?? 8.4) / 24,
    rainMinGap: (climate?.rainMinGapDays ?? 6) * DAY_LENGTH,
    rainMaxGap: (climate?.rainMaxGapDays ?? 8) * DAY_LENGTH,
    rainMinDur: 30,
    rainMaxDur: 90,
    yearLengthDays: climate?.yearLengthDays,
    startDayOfYear: climate?.startDayOfYear,
    onRainStart: () => {
      // Heavy storms drown 15% of above-ground foragers on both teams.
      antWorld.cullFraction(0.15);
    },
    // Survival events — cold snap (once/winter) + heat wave (once/summer).
    // Warning fires the auto-recall; active sets the damage gate; end
    // lifts both. Total in-the-nest window = 30 s warning + 60 s active.
    onColdSnapWarning: () => antWorld.setRecall(95),
    onColdSnapStart:   () => antWorld.setColdSnapActive(true),
    onColdSnapEnd:     () => { antWorld.setColdSnapActive(false); antWorld.clearRecall(); },
    onHeatWaveWarning: () => antWorld.setRecall(95),
    onHeatWaveStart:   () => antWorld.setHeatWaveActive(true),
    onHeatWaveEnd:     () => { antWorld.setHeatWaveActive(false); antWorld.clearRecall(); },
  });
  const weatherHud = new WeatherHUD(weather);
  const keyHelp = new KeyHelp({ minimapSize });
  buildWorldPicker();

  // Underground mode — descent/ascent + chamber click-to-move.
  const nestMode = new NestMode(
    { config: redNest,   interior: redInterior },
    { config: blackNest, interior: blackInterior },
    camera,
    {
      getPlayerPos: () => {
        const p = antWorld.ants[antWorld.playerId];
        return p ? { x: p.x, y: p.y, z: p.z } : { x: 0, y: 0, z: 0 };
      },
      teleportPlayer: (x, z) => {
        const p = antWorld.ants[antWorld.playerId];
        if (!p) return;
        p.x = x;
        p.z = z;
        p.vx = 0;
        p.vz = 0;
        p.goal = null;
      },
      restoreSurfaceCamera: () => {
        camera.setFollow(null);
        camera.setAntCam(null);
      },
      playerTeam: 0, // yellow ant is always on team 0 (red)
      setOutdoorAmbianceMuted: (m) => audio.setOutdoorAmbianceMuted(m),
      countFollowers: () => {
        let n = 0;
        for (const a of antWorld.ants) {
          if (a.alive && a.team === 0 && a.role === 'follower') n++;
        }
        return n;
      },
      onPlayerKilledUnderground: () => {
        // Treat the same as dying on the surface — also ensures respawn clock.
        const p = antWorld.ants[antWorld.playerId];
        if (p && p.alive) {
          p.hp = 0;
        }
      },
      onQueenKilled: (side) => {
        const colony = side === 'red' ? redColony : blackColony;
        const queenId = colony.queenId;
        const queen = antWorld.ants[queenId];
        if (queen && queen.alive) queen.hp = 0;
        colony.onQueenDeath();
      },
      surfaceRoots: [
        terrain.mesh,
        skirt.mesh,
        grass.mesh,
        rocks.group,
        foodMarkers.group,
        colony.group,
        spiderMesh.group,
        grasshopperMesh.group,
        rolyPolyMesh.group,
        lizardMesh.group,
        redNestMarker.group,
        blackNestMarker.group,
        magGlass.group,
        bugSpray.group,
        mower.group,
        foot.group,
        ...customSims.map((cs) => cs.mesh.group),
        ...customPlants.map((p) => p.group),
        pebbleMesh.group,
      ],
    },
  );
  nestMode.scene = world.scene;
  input.setModeOverride(
    () => ({
      active: nestMode.isUnderground,
      walkables: nestMode.walkables,
      exitPos: nestMode.exitRingMarker,
    }),
    (p, exitHit) => nestMode.handleClick(p, exitHit),
  );
  buildTouchControls({
    toggleColonyHud: () => colonyHud.toggle(),
  });
  buildFullscreenButton();
  buildSoundControls(audio);

  // Dev console gating. The API below is attached to `window.ant` ONLY for
  // browsers that have the local-storage flag `antDev === "1"`. A random
  // visitor types `ant.help()` and gets an undefined error — nothing leaks.
  //
  // To enable it on your own browser, visit once with `?dev=1` in the URL
  // (persisted via localStorage), then drop the param. `?dev=0` turns it
  // back off. Works on any device you own; public traffic is unaffected.
  try {
    const params = new URLSearchParams(window.location.search);
    const devParam = params.get('dev');
    if (devParam === '1') localStorage.setItem('antDev', '1');
    else if (devParam === '0') localStorage.removeItem('antDev');
  } catch { /* private-mode / storage blocked — fall through */ }
  let devEnabled = false;
  try { devEnabled = localStorage.getItem('antDev') === '1'; } catch { devEnabled = false; }
  if (devEnabled) attachDevConsole();

  function attachDevConsole() {
  (window as unknown as { ant: Record<string, unknown> }).ant = {
    help() {
      const lines = [
        'Ant Sim console API — current handles:',
        '  ant.help()                 — this message',
        '  ant.summon(n=10)           — promote n nearest normals to followers',
        '  ant.release(frac=1)        — release frac (0..1) of followers',
        '  ant.recall()               — toggle U-key nest recall',
        '  ant.spawnFood(n=50, r=2)   — drop n food-piles around the player within r m',
        '  ant.killArea(r=3)          — kill all non-player ants within r m of the player',
        '  ant.cull(frac=0.2)         — rain-style cull of frac of living ants (player exempt)',
        '  ant.teleport(x, z)         — move player ant to world (x, z)',
        '  ant.time(hour=12)          — set time-of-day (0..24)',
        '  ant.temp(c)                — force temperature in °C',
        '  ant.weather("sunny"|"rainy"|"storm"|"night")  — quick weather presets',
        '  ant.population()           — per-team alive count + castes',
        '  ant.stockpile()            — food stockpile per team',
        '  ant.mower()                — force the lawn mower to start now',
        '  ant.lizard()               — spawn the lizard now (if off-map)',
        '  ant.invade()               — walk player straight to enemy nest',
        '  ant.debug()                — expose raw handles under ant.$ (antWorld, weather, etc.)',
        'Player-ant exempt from most world-state operations.',
      ];
      console.log(lines.join('\n'));
    },
    summon(n = 10) { return antWorld.summonFollowers(n); },
    release(frac = 1) { return antWorld.releaseFollowers(frac); },
    recall() { return antWorld.toggleNestRecall(); },
    spawnFood(n = 50, r = 2) {
      const p = antWorld.ants[antWorld.playerId];
      if (!p) return 'no player';
      let dropped = 0;
      for (let i = 0; i < n; i++) {
        const ang = Math.random() * Math.PI * 2;
        const rr = Math.random() * r;
        food.spawn(p.x + Math.cos(ang) * rr, p.z + Math.sin(ang) * rr, 20, 5, 'pile');
        dropped++;
      }
      return `spawned ${dropped} piles`;
    },
    killArea(r = 3) {
      const p = antWorld.ants[antWorld.playerId];
      if (!p) return 'no player';
      return antWorld.killArea(p.x, p.z, r);
    },
    cull(frac = 0.2) { return antWorld.cullFraction(frac); },
    teleport(x: number, z: number) {
      const p = antWorld.ants[antWorld.playerId];
      if (!p) return 'no player';
      p.x = x; p.z = z; p.goal = null; p.vx = 0; p.vz = 0;
      return `player at (${x.toFixed(2)}, ${z.toFixed(2)})`;
    },
    time(hour = 12) {
      // WeatherState is frozen-ish — mutate the mutable fields directly.
      (weather.state as { timeOfDay: number }).timeOfDay = Math.max(0, Math.min(24, hour)) / 24;
      return weather.state.timeOfDay;
    },
    temp(c: number) {
      (weather.state as { temperature: number }).temperature = c;
      antWorld.setTemperature(c);
      return c;
    },
    weather(preset: string) {
      const s = weather.state as { timeOfDay: number; rain: number };
      switch (preset) {
        case 'sunny': s.timeOfDay = 0.5; s.rain = 0; break;
        case 'rainy': s.rain = 0.7; break;
        case 'storm': s.rain = 1.0; break;
        case 'night': s.timeOfDay = 0.0; break;
        default: return 'presets: sunny | rainy | storm | night';
      }
      return preset;
    },
    population() {
      const teams: Record<number, Record<string, number>> = { 0: {}, 1: {} };
      for (const a of antWorld.ants) {
        if (!a.alive) continue;
        const t = teams[a.team];
        t[a.casteType] = (t[a.casteType] || 0) + 1;
        t.total = (t.total || 0) + 1;
      }
      return teams;
    },
    stockpile() { return { red: redColony.stockpile, black: blackColony.stockpile }; },
    mower() {
      // Nudge the schedule so the next update() triggers a sweep even if
      // the date / time-of-day gate would normally block it.
      (mower as unknown as { nextSweepDay: number }).nextSweepDay = 0;
      return 'mower will start on next eligible tick';
    },
    lizard() { lizardSim.state.nextSpawnDay = 0; return 'will spawn on next tick'; },
    invade() {
      const p = antWorld.ants[antWorld.playerId];
      if (!p) return 'no player';
      const enemy = p.team === 0 ? blackNest : redNest;
      antWorld.setPlayerGoal(enemy.x, enemy.z);
      return `walking to enemy nest at (${enemy.x}, ${enemy.z})`;
    },
    debug() {
      const api = (window as unknown as { ant: { $?: unknown } }).ant;
      api.$ = { antWorld, weather, spiderSim, grasshopperSim, lizardSim, rolyPolySim, mower, food, redColony, blackColony, nestMode };
      return Object.keys(api.$ as Record<string, unknown>);
    },
  };
  console.log('%cAnt Sim dev console ready. Type `ant.help()` for commands.', 'color:#6ab4ff;font-weight:600;');
  }

  const rain = new Rain(weather, { area: 40, maxDrops: 600 });
  world.scene.add(rain.group);
  // Rain was declared after NestMode to keep that section contiguous; push it
  // into the nest mode's surface-root toggle list now so it hides underground.
  nestMode.addSurfaceRoot(rain.group);

  // Post temporarily disabled to isolate a shadow-rendering regression.
  void Post;
  const post: { render: (dt: number) => void } = { render: () => {} };
  void post;

  const stats = new Stats();
  stats.showPanel(0);
  stats.dom.style.cssText = 'position:fixed;top:8px;left:8px;z-index:10;';
  document.body.appendChild(stats.dom);

  loading.classList.add('done');
  audio.playAmbient();

  // Always-on perf diagnostic: window.perf() prints renderer.info +
  // scene-graph counts so we can see what's actually being drawn.
  // No localStorage gate — exposing read-only renderer stats is harmless.
  (window as unknown as { perf: () => Record<string, unknown> }).perf = () => {
    const info = world.renderer.info;
    let objs = 0, visible = 0, skinned = 0, instanced = 0;
    world.scene.traverse((o) => {
      objs++;
      if (o.visible) visible++;
      const m = o as unknown as { isSkinnedMesh?: boolean; isInstancedMesh?: boolean };
      if (m.isSkinnedMesh) skinned++;
      if (m.isInstancedMesh) instanced++;
    });
    const out = {
      fps: stats.dom.children[0]?.textContent ?? '',
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      sceneObjects: objs,
      visibleObjects: visible,
      skinnedMeshes: skinned,
      instancedMeshes: instanced,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
    };
    console.log('=== Game perf snapshot ===');
    console.table(out);
    return out;
  };

  // First-boot: if the server had no world config, save the one this client
  // just generated so every subsequent visitor sees the same landscape.
  if (!serverWorld) {
    const newWorld = defaultWorldConfig();
    newWorld.terrainSeed = 1337;
    newWorld.nests = [
      { team: 0, x: redNest.x,   z: redNest.z,   radius: redNest.radius },
      { team: 1, x: blackNest.x, z: blackNest.z, radius: blackNest.radius },
    ];
    newWorld.rocks = rocks.generatedPlacements;
    newWorld.grass = grass.generatedPlacements;
    newWorld.foodPiles = initialFoodPiles;
    saveWorld(newWorld).catch((e) => console.warn('saveWorld failed:', e));
  }

  setTimeout(() => {
    const p = antWorld.ants[antWorld.playerId];
    if (p) camera.flyTo(p.x, p.y, p.z, 0.7, { x: 0.8, y: 0.7, z: 0.8 });
  }, 300);

  let foodResyncTimer = 0;
  // Every 2 in-game days each nest tacks on a new tunnel + chamber so the
  // underground tree visibly grows as the colony ages.
  let nestGrowTimer = 0;
  const NEST_GROW_INTERVAL = 2 * DAY_LENGTH;

  const clock = new THREE.Clock();
  function frame() {
    stats.begin();
    const dt = Math.min(clock.getDelta(), 1 / 30);
    const elapsed = clock.elapsedTime;

    camera.update(dt);
    nestMode.update(dt);
    // Pause all sim while the KeyHelp modal is open. Rendering continues
    // so the last frame stays visible (frozen) during the menu.
    const paused = keyHelp.isPaused;
    if (!paused) weather.update(dt, elapsed);
    world.applyWeather(weather.state, 60, {
      x: camera.controls.target.x,
      z: camera.controls.target.z,
    });
    antWorld.setTemperature(weather.state.temperature);
    antWorld.setPlayerUnderground(nestMode.isUnderground);

    // Seasonal activity gate — lizards, grasshoppers, and the mower don't
    // run between Oct 1 and Mar 31 (DOY 274-365 + 1-90). They come back at
    // half the usual rate in the shoulder months (Apr-May, late Sep).
    const doy = weather.state.dayOfYear;
    let seasonalActivity = 1;
    if (doy >= 274 || doy <= 90) seasonalActivity = 0;         // Oct-Mar: dormant
    else if (doy < 152) seasonalActivity = 0.5;                // Apr 1 – May 31
    else if (doy >= 259) seasonalActivity = 0.5;               // Sep 16 – Sep 30
    // else Jun 1 – Sep 15 = full rate.
    const seasonalActive = seasonalActivity > 0;
    // For half rate, skip update roughly 50% of ticks — cheap approximation
    // that halves both spawn rates and aggregate presence.
    const runSeasonal = seasonalActive && (seasonalActivity === 1 || Math.random() < 0.5);
    // Per-world entity-enabled flags gate visibility + sim updates. A disabled
    // creature hides its mesh AND its sim never ticks (the sim just holds its
    // initial zero-count / not-alive state). These all come from the catalog
    // refs resolved during boot.
    const ghEnabled = grasshopperRef.enabled;
    const lzEnabled = lizardEnabled;
    const mwEnabled = mowerEnabled;
    grasshopperMesh.group.visible = ghEnabled && seasonalActive;
    // Force the grasshopper roster to dead while off-season. The sim is
    // paused (no update tick), so without this their last `alive=true`
    // state lingers — minimap dots stay drawn and follower combat still
    // engages invisible hoppers. Respawn happens naturally once
    // seasonalActive flips back on and the sim ticks again.
    if (!seasonalActive) {
      for (const g of grasshopperSim.hoppers) {
        if (g.alive) {
          g.alive = false;
          g.respawnT = 1;  // small positive so the sim respawn-tick triggers next season
        }
      }
    }
    lizardMesh.group.visible = lzEnabled && seasonalActive && lizardSim.state.alive;
    mower.group.visible = mwEnabled && seasonalActive && mower.active;
    grass.update(elapsed, camera.camera, weather.state.wind);
    if (!paused) rain.update(dt, camera.camera);
    if (!paused) antWorld.update(dt, elapsed);
    const rainDecayMul = 1 + weather.state.rain * 2;
    pheromones[0].setDecayMultiplier(rainDecayMul);
    pheromones[1].setDecayMultiplier(rainDecayMul);
    if (!paused) {
      pheromones[0].update(dt);
      pheromones[1].update(dt);
      redColony.update(dt, elapsed, antWorld);
      blackColony.update(dt, elapsed, antWorld);
      if (spiderRef.enabled) spiderSim.update(dt);
      if (runSeasonal && ghEnabled) grasshopperSim.update(dt);
      if (rolyPolyRef.enabled) rolyPolySim.update(dt);
      for (const { sim } of customSims) sim.update(dt);
      if (runSeasonal && lzEnabled) lizardSim.update(dt, Math.floor(elapsed / DAY_LENGTH));
      else if (!seasonalActive && lizardSim.state.alive) {
        // Force hibernation — lizard isn't around outside April..September.
        lizardSim.state.alive = false;
      }
      magGlass.update(
        dt,
        (x, z, r, dps, dt) => antWorld.damageArea(x, z, r, dps, dt),
        (x, z, r, f) => antWorld.scatterArea(x, z, r, f),
      );
      bugSpray.update(
        dt,
        (x, z, r, dps, bdt) => antWorld.damageArea(x, z, r, dps, bdt),
        (x, z, r, f) => antWorld.scatterArea(x, z, r, f),
      );
      if (runSeasonal && mwEnabled) mower.update(
        dt,
        elapsed,
        weather.state.timeOfDay,
        Math.floor(elapsed / DAY_LENGTH),
        (x, z, r, dps, mdt) => antWorld.damageArea(x, z, r, dps, mdt),
        (x, z, r, f) => antWorld.scatterArea(x, z, r, f),
        (x, z, r) => {
          antWorld.killArea(x, z, r);
          spiderSim.killArea(x, z, r);
          grasshopperSim.killArea(x, z, r);
          food.consumeArea(x, z, r);
        },
      );
      audio.setMowerActive(runSeasonal && mower.active);
      foot.update(
        dt,
        elapsed,
        (x, z, r, dps, fdt) => antWorld.damageArea(x, z, r, dps, fdt),
        (x, z, r, f) => antWorld.scatterArea(x, z, r, f),
      );
    }
    // Renderers keep updating (matrices + mixers) so the paused frame still composes.
    colony.update(dt, elapsed, camera.camera, nestMode.isUnderground);
    spiderMesh.update(dt, elapsed);
    grasshopperMesh.update(dt, elapsed);
    rolyPolyMesh.update(dt);
    for (const { mesh } of customSims) mesh.update(dt);
    pebbleMesh.update(antWorld.ants);
    // Mirror the recall order into the nest interiors so their residents
    // funnel toward the queen chamber while U is active.
    redInterior.setRecalled(antWorld.recallActive);
    blackInterior.setRecalled(antWorld.recallActive);
    lizardMesh.update(dt);
    brood.update();
    redNestMarker.update(dt);
    blackNestMarker.update(dt);

    nestGrowTimer += dt;
    if (nestGrowTimer > NEST_GROW_INTERVAL) {
      redInterior.grow();
      blackInterior.grow();
      nestGrowTimer = 0;
    }

    foodResyncTimer += dt;
    if (foodResyncTimer > 0.25) {
      foodMarkers.syncInstances();
      foodResyncTimer = 0;
    }

    // Pick an ambient layer to crossfade to based on the current scene
    // state. Priority: nest (player underground) > fight (heavy combat)
    // > rain > wind (gusts) > night (sun down) > day. Slots without an
    // override URL configured are skipped — runtime falls through to
    // the next priority.
    {
      const ws = weather.state;
      let bestSlot: 'ambient' | 'ambientNight' | 'ambientWind' | 'ambientNest' | 'ambientRain' | 'ambientFight' = 'ambient';
      const has = (k: typeof bestSlot) => !!serverWorld?.soundOverrides?.[k];
      const isNight = ws.sunIntensity < 0.05;
      const isWindy = ws.wind > 2.2;
      const isRaining = ws.rain > 0.25;
      let fighters = 0;
      for (let i = 0; i < antWorld.ants.length && fighters < 6; i++) {
        if (antWorld.ants[i].alive && antWorld.ants[i].attacking) fighters++;
      }
      const isFight = fighters >= 5;
      if (nestMode.isUnderground && has('ambientNest'))      bestSlot = 'ambientNest';
      else if (isFight && has('ambientFight'))               bestSlot = 'ambientFight';
      else if (isRaining && has('ambientRain'))              bestSlot = 'ambientRain';
      else if (isWindy && has('ambientWind'))                bestSlot = 'ambientWind';
      else if (isNight && has('ambientNight'))               bestSlot = 'ambientNight';
      else                                                    bestSlot = 'ambient';
      audio.setActiveAmbient(bestSlot);
    }

    minimap.draw();
    popHud.update();
    playerHud.update();
    colonyHud.update();
    weatherHud.update();
    // Three.js auto-updates the shadow map when the camera or shadow
    // light moves. Forcing it every frame here was a leftover diagnostic
    // and was the single biggest perf hit — re-rendering 60+ skinned
    // ants + 4 k grass tufts + rocks + custom plants through the
    // shadow pass on EVERY frame. Removed.
    void post;
    world.renderer.render(world.scene, camera.camera);
    stats.end();
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

boot().catch((err) => {
  console.error(err);
  const loading = document.getElementById('loading');
  if (loading) loading.textContent = 'failed to load — check console';
});

/** Small top-centre picker that lets the player switch worlds without opening
 *  the editor. Selecting a world updates the active pointer and reloads. */
async function buildWorldPicker(): Promise<void> {
  let list: { worlds: string[]; active: string | null } = { worlds: [], active: null };
  try {
    const res = await fetch('/api/worlds', { cache: 'no-cache' });
    if (res.ok) list = await res.json();
  } catch { /* no server, skip */ }
  if (list.worlds.length === 0) return;

  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'position:fixed', 'top:8px', 'left:8px',
    'background:rgba(15,20,25,0.78)',
    'color:#eee', 'border-radius:8px',
    'padding:4px 8px', 'font:11px system-ui,sans-serif',
    'z-index:5',
    'display:flex', 'gap:6px', 'align-items:center',
    'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
  ].join(';');
  const lab = document.createElement('span');
  lab.textContent = 'World';
  lab.style.opacity = '0.7';
  const sel = document.createElement('select');
  sel.style.cssText = 'background:#1a1d22;color:#eee;border:1px solid #333;border-radius:4px;padding:1px 4px;';
  for (const slug of list.worlds) {
    const opt = document.createElement('option');
    opt.value = slug;
    opt.textContent = slug;
    if (slug === list.active) opt.selected = true;
    sel.appendChild(opt);
  }
  sel.addEventListener('change', async () => {
    const slug = sel.value;
    await fetch('/api/worlds-active', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: slug }),
    });
    location.reload();
  });
  wrap.appendChild(lab);
  wrap.appendChild(sel);
  document.body.appendChild(wrap);
}
